import { UpsellGuard } from '@components'

import { Switch } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Scope } from '../../../../../../../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../../../../../../../../../hooks'
import { unsafeReplaceHistoryState } from '../../../../../../../../../../../../../../utils/history'
import { getOrganizationViewUrl } from '../../../../../../../../../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../../../../../../../utils/routing/reactRoute'

export const RecurringInvoiceToggle = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { pathname } = useLocation()
  const { organization } = useUserOrganization()

  const recurringInvoiceDocumentRouteBase = getInvoiceDocumentRouteBase(InvoiceDocumentType.RecurringInvoice)
  const isCurrentPathRecurringInvoices = pathname.includes(recurringInvoiceDocumentRouteBase)

  const handleRecurringInvoiceToggle = useCallback(() => {
    const newDocumentRouteBase = getInvoiceDocumentRouteBase(
      isCurrentPathRecurringInvoices ? InvoiceDocumentType.Invoice : InvoiceDocumentType.RecurringInvoice,
    )
    const newPath = getOrganizationViewUrl(
      organization?.url,
      reactRoute.organization.invoices.getNewDocumentRoute(newDocumentRouteBase),
    )

    if (!isCurrentPathRecurringInvoices) {
      track('XXX - Invoicing Core - Recurring invoice editor opened', { triggeredBy: 'toggle' })
    }

    // We want to preserve InvoiceEditorFormContext state when switching between invoices/recurring-invoices routes.
    // That is why we use window._sharedHistory.replaceState to workaround Ember resetting context with transitionTo.
    // This util is not advised for further usage and should be revised when it is decided to get rid of Ember.
    unsafeReplaceHistoryState(newPath)
  }, [isCurrentPathRecurringInvoices, organization?.url, track])

  return (
    <UpsellGuard scope={Scope.RecurringInvoiceUpsell} trackingContext={TrackingContext.CreateRecurringInvoice}>
      <Switch checked={isCurrentPathRecurringInvoices} onChange={handleRecurringInvoiceToggle}>
        {t('recurring_invoices.editor.toggle.label')}
      </Switch>
    </UpsellGuard>
  )
}
